/** Defines Aqua 13.6 input-value semantics shared by Order Entry controls and request serialization. */

/** Compares provider-prefixed and canonical Aqua parameter names without changing unrelated IDs. */
export function normalizeAquaParameterId(parameterId: string): string {
  const normalized = parameterId.replaceAll(/[^a-zA-Z\d]/g, '').toUpperCase().replace(/^JPMAQUA/, '');
  if (normalized === 'MAXPERCENTVOLUME') {
    return 'MAXPARTICIPATIONPCT';
  }

  return normalized === 'STOPPX' ? 'STOPPRICE' : normalized;
}

const aquaAlgoIdentities = new Set(['AQUA', 'JPM_AQUA', 'JPMorgan Aqua'].map(identity =>
  identity.replaceAll(/[^a-zA-Z\d]/g, '').toUpperCase()));

/** Identifies every provider-facing Aqua strategy name accepted by linked order records. */
export function isAquaAlgoIdentity(value: unknown): boolean {
  return typeof value === 'string'
    && aquaAlgoIdentities.has(value.replaceAll(/[^a-zA-Z\d]/g, '').toUpperCase());
}

/** Converts actual Aqua boolean values to the broker's Y/N representation while preserving absence. */
export function normalizeAquaParameterValue(parameterId: string, value: unknown): unknown {
  const id = normalizeAquaParameterId(parameterId);
  return (id === 'COMPLETEBYENDTIME' || id === 'FSPM') && typeof value === 'boolean'
    ? (value ? 'Y' : 'N')
    : value;
}

/** Recognizes empty optional controls separately from false and numeric zero. */
function isAbsent(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === 'string' && ['', '-'].includes(value.trim()));
}

/** Resolves Aqua's supported order-type labels and enum values to FIX 40 values. */
export function getAquaOrderType(value: unknown): string | undefined {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value >= 1 && value <= 4 ? String(value) : undefined;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  if (/\d/.test(trimmedValue) && !/[a-zA-Z]/.test(trimmedValue)) {
    return /^[1-4]$/.test(trimmedValue) ? trimmedValue : undefined;
  }

  const normalized = trimmedValue.replaceAll(/[^a-zA-Z\d]/g, '').toUpperCase().replace(/^ORDERTYPE/, '');
  const values: Record<string, string> = {
    MARKET: '1', MKT: '1', LIMIT: '2', LMT: '2', STOP: '3', STP: '3', STOPLIMIT: '4', STPLMT: '4',
  };
  return values[normalized] ?? (/^[1-4]$/.test(normalized) ? normalized : undefined);
}

const aquaOrderTypeError = 'Aqua Order Type must be Market, Limit, Stop, or Stop Limit.';
const aquaTimeInForceError = 'Aqua Time In Force must be Day.';

/** Rejects malformed supplied Aqua values; optional blanks remain omitted and never become defaults. */
function getParameterError(parameterId: string, rawValue: unknown): string | undefined {
  const id = normalizeAquaParameterId(parameterId);
  if (rawValue === undefined || rawValue === null) {
    if (id === 'ORDERTYPE') {
      return aquaOrderTypeError;
    }

    return id === 'TIMEINFORCE' ? aquaTimeInForceError : undefined;
  }

  if (isAbsent(rawValue) && !['ORDERTYPE', 'TIMEINFORCE', 'URGENCY'].includes(id)) {
    return undefined;
  }

  if (id === 'STARTTIME') {
    return typeof rawValue === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(rawValue)
      ? undefined
      : 'Aqua Start Time must be a valid HH:MM value.';
  }

  const value = normalizeAquaParameterValue(parameterId, rawValue);
  if (id === 'ORDERTYPE') {
    return getAquaOrderType(value) === undefined ? aquaOrderTypeError : undefined;
  }

  if (id === 'TIMEINFORCE') {
    return value === 0 || value === '0' || value === 'Day' ? undefined : aquaTimeInForceError;
  }

  if (id === 'URGENCY') {
    return typeof value === 'string' && ['Low', 'Medium', 'High', 'GetDone'].includes(value)
      ? undefined
      : 'Aqua Urgency must be Low, Medium, High, or GetDone.';
  }

  if (id === 'COMPLETEBYENDTIME' || id === 'FSPM') {
    return value === 'Y' || value === 'N' ? undefined : 'Aqua completion controls must be Y or N.';
  }

  if (['MAXPARTICIPATIONPCT', 'WOULDPRICE', 'LIMITPRICE', 'STOPPRICE'].includes(id)) {
    const numericValue = typeof value === 'number' || typeof value === 'string' ? Number(value) : Number.NaN;
    if (!Number.isFinite(numericValue)) {
      return 'Aqua parameter must be a finite number.';
    }

    if (id === 'MAXPARTICIPATIONPCT' && (numericValue < 0 || numericValue > 100)) {
      return 'Aqua Max % Volume must be between 0 and 100.';
    }
  }

  return undefined;
}

/** Validates supplied parameters and conditional stop/limit prices, returning errors keyed to their controls. */
export function validateAquaParameters(parameters: Record<string, unknown>): Record<string, string> {
  const errors: Record<string, string> = {};
  const normalized = new Map<string, unknown>();
  for (const [key, value] of Object.entries(parameters)) {
    const normalizedId = normalizeAquaParameterId(key);
    const existingValue = normalized.get(normalizedId);
    // A retained blank provider alias must not replace a value entered through the rendered canonical control.
    if (!normalized.has(normalizedId) || !isAbsent(value) || isAbsent(existingValue)) {
      normalized.set(normalizedId, value);
    }

    const error = getParameterError(key, value);
    if (error) {
      errors[key] = error;
    }
  }

  if (!normalized.has('ORDERTYPE')) {
    errors.orderType = aquaOrderTypeError;
  }

  if (!normalized.has('TIMEINFORCE')) {
    errors.timeInForce = aquaTimeInForceError;
  }

  const orderType = getAquaOrderType(normalized.get('ORDERTYPE'));
  if ((orderType === '2' || orderType === '4') && isAbsent(normalized.get('LIMITPRICE'))) {
    errors.limitPrice = 'Limit price must be a value';
  }

  if ((orderType === '3' || orderType === '4') && isAbsent(normalized.get('STOPPRICE'))) {
    errors.stopPrice = 'Stop price is required.';
  }

  return errors;
}
